import { QueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { InputNumber } from 'primereact/inputnumber'
import { Nullable } from 'primereact/ts-helpers'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Spinner } from '../../App/Components'
import { Flex } from '../../Common/Flex'
import { useWindowDimensions } from '../../Common/Hooks/useWindowDimensions'
import { RadioGroup } from '../../Common/RadioGroup'
import { H2, H3, Paragraph } from '../../Common/Typography'
import { useGetPersonDataQuery } from '../util/getPersonInfo'

import {
  ApiPersonContract,
  useCreatePersonContractMutation,
  useUpdatePersonContractMutation,
} from './util/usePersonContractQuery'

type CreateProps = { type: 'create' }
type EditProps = { type: 'edit'; contract: ApiPersonContract }

type Props = { personUuid: string } & (CreateProps | EditProps)

type ContractTypes = 'fixed' | 'permanent'
type RadioOption = { label: string; value: ContractTypes }

const saveButton = { maxWidth: '90px', margin: '24px' }
const backButton = { maxWidth: '60px', margin: '8px' }

const radioOptions: RadioOption[] = [
  { label: 'Permanent', value: 'permanent' },
  { label: 'Fixed', value: 'fixed' },
]

export const ContractForm = (props: Props) => {
  const { type } = props

  const [weeklyHours, setWeeklyHours] = useState<number>(38)
  const [weeklyMinutes, setWeeklyMinutes] = useState<number>(0)
  const [startDate, setStartDate] = useState<Nullable<Date>>(null)
  const [endDate, setEndDate] = useState<Nullable<Date>>(null)
  const [contractType, setContractType] = useState<ContractTypes>('permanent')

  const history = useHistory()
  const { t } = useTranslation()
  const queryClient = new QueryClient()
  const windowWidth = useWindowDimensions().windowWidth

  const { status, data } = useGetPersonDataQuery(props.personUuid)
  const createContractMutation = useCreatePersonContractMutation(queryClient)
  const editContractMutation = useUpdatePersonContractMutation(queryClient)

  useEffect(() => {
    if (type === 'edit') {
      const contract = props.contract
      const getEndDate = contract.end_date ? new Date(contract.end_date) : null
      setStartDate(new Date(contract.start_date))
      setEndDate(getEndDate)
      setWeeklyHours(Math.floor(contract.contract_week_minutes / 60))
      setWeeklyMinutes(Math.floor(contract.contract_week_minutes % 60))
      setContractType(getEndDate ? 'fixed' : 'permanent')
    }
  }, [props])

  useMemo(() => {
    if (createContractMutation.isSuccess || editContractMutation.isSuccess) {
      history.push(`/hr/persons/contracts/${props.personUuid}`)
    }
  }, [
    createContractMutation.isSuccess,
    editContractMutation.isSuccess,
    history,
    props.personUuid,
  ])

  if (
    status === 'pending' ||
    createContractMutation.isPending ||
    editContractMutation.isPending
  ) {
    return <Spinner />
  }

  if (status === 'error') {
    return <div>{t('error')}</div>
  }

  const personFullName = `${data.first_name} ${data.last_name || ''}`
  const contractTitle =
    type === 'create' ? t('Create contract for') : t('Edit contract for')

  const handleSubmit = () => {
    if (!weeklyHours || !startDate) {
      toast.error(t('Please fill in all required fields'))
      return
    }

    const formattedStartDate = moment(startDate).format('YYYY-MM-DD')
    const formattedEndDate = endDate
      ? moment(endDate).format('YYYY-MM-DD')
      : null

    const dataObject = {
      start_date: formattedStartDate,
      end_date: contractType === 'permanent' ? null : formattedEndDate,
      contract_week_minutes: weeklyHours * 60 + weeklyMinutes,
    }

    if (type === 'create') {
      const createData = {
        ...dataObject,
        person_uuid: props.personUuid,
      }
      createContractMutation.mutate(createData)
    }
    if (type === 'edit') {
      editContractMutation.mutate({
        uuid: props.contract.uuid,
        contractData: dataObject,
      })
    }
  }

  return (
    <Flex flexDirection={'column'}>
      <Button
        style={backButton}
        onClick={() => history.goBack()}
        className="fa fa-arrow-left"
      />
      <H2 m={2}>{`${contractTitle} ${personFullName}`}</H2>
      <Flex flexDirection={windowWidth < 560 ? 'column' : 'row'}>
        <Flex m={3} maxWidth={'200px'} flexDirection={'column'}>
          <Paragraph>{t('Weekly hours')}</Paragraph>
          <InputNumber
            value={weeklyHours}
            onChange={e => setWeeklyHours(e.value || 1)}
            max={80}
            min={1}
            disabled={type === 'edit'}
          />
        </Flex>
        <Flex m={3} maxWidth={'200px'} flexDirection={'column'}>
          <Paragraph>{t('Minutes')}</Paragraph>
          <InputNumber
            value={weeklyMinutes}
            onChange={e => setWeeklyMinutes(e.value || 0)}
            max={60}
            min={0}
            disabled={type === 'edit'}
          />
        </Flex>
      </Flex>

      <Flex flexDirection={'column'} m={3}>
        <H3>{t('Contract type')}</H3>
        <RadioGroup
          options={radioOptions}
          value={contractType}
          onChange={(e: ContractTypes) => setContractType(e)}
        />
      </Flex>

      <Flex flexDirection={windowWidth < 560 ? 'column' : 'row'}>
        <Flex m={3} maxWidth={'200px'} flexDirection={'column'}>
          <Paragraph>{t('Start date')}</Paragraph>
          <Calendar
            dateFormat={'dd/mm/yy'}
            value={startDate}
            onChange={e => setStartDate(e.value)}
          />
        </Flex>
        {contractType === 'permanent' || (
          <Flex m={3} maxWidth={'200px'} flexDirection={'column'}>
            <Paragraph>{t('End date')}</Paragraph>
            <Calendar
              dateFormat={'dd/mm/yy'}
              value={endDate}
              onChange={e => setEndDate(e.value)}
            />
          </Flex>
        )}
      </Flex>

      <Button
        style={saveButton}
        onClick={() => handleSubmit()}
        label={t('Save')}
      />
    </Flex>
  )
}
